'use client';

import { useState, useEffect, createContext, type ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';
import { validateEmail } from '@/_utils/validations';
import type {
  LoginProviderProps,
  LoginFormState,
  StandaloneSigninMethodState,
} from './LoginProvider.types';

const DEFAULT_TWO_FACTOR_MESSAGE: string =
  'Two-factor authentication is required. Please enter your verification code.';

const LoginContext = createContext<LoginProviderProps>({
  loginState: 'login',
  twoFactorMessage: DEFAULT_TWO_FACTOR_MESSAGE,
  phoneNumber: '',
  previousLoginState: 'login',
  setLoginState: () => null,
  setPhoneNumber: () => null,
  setPreviousLoginState: () => null,
  setStandaloneState: () => null,
  setTwoFactorMessage: () => null,
  setUid: () => null,
  setUsername: () => null,
  standaloneState: undefined,
  uid: undefined,
  username: '',
});

const LoginProvider = (props: { children: ReactNode }) => {
  const [username, setUsername] = useState<string>('');
  const [twoFactorMessage, setTwoFactorMessage] = useState<string>(
    DEFAULT_TWO_FACTOR_MESSAGE,
  );
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [uid, setUid] = useState<string | undefined>(undefined);
  const [loginState, setLoginState] = useState<LoginFormState>('login');
  const [previousLoginState, setPreviousLoginState] =
    useState<LoginFormState>('login');
  const [standaloneState, setStandaloneState] = useState<
    StandaloneSigninMethodState | undefined
  >(undefined);

  const onSetLoginState = (newState: unknown) => {
    console.debug('--> onSetLoginState', { newState });

    setStandaloneState(newState as StandaloneSigninMethodState);
    setLoginState(newState as LoginFormState);
  };

  const searchParams = useSearchParams();
  const emailParam = searchParams.get('email');

  useEffect(() => {
    const isValidEmail: boolean = validateEmail(emailParam);

    if (isValidEmail) {
      setUsername(emailParam as string);
    }
  }, [emailParam]);

  return (
    <LoginContext
      value={{
        loginState,
        phoneNumber,
        previousLoginState,
        setLoginState: onSetLoginState,
        setPhoneNumber,
        setPreviousLoginState,
        setStandaloneState: onSetLoginState,
        setTwoFactorMessage,
        setUid,
        setUsername,
        standaloneState,
        twoFactorMessage,
        uid,
        username,
      }}
    >
      {props.children}
    </LoginContext>
  );
};

export { LoginContext, LoginProvider };
